import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  fetchOrderTicketsGuest,
  fetchOrderTicketsWithTicketId,
} from "../../api/orderApi";
import Constant from "../../util/constant";
import EntryQrAccordion from "./EntryQrAccordion";
import {
  guestTokenFromSearchParams,
  guestTokenFromWindowLocation,
  normalizeTicketsView,
  pickEventImageSrc,
  pickEventStartRaw,
  pickEventVenue,
  pickBookingRefSummary,
  pickQuantityTypeLine,
} from "./orderTicketsShared";
import "./myTickets.css";

/**
 * Shared ticket fetch + UI for /my-tickets/... and /{prefix}/{slug}/my-tickets/...
 *
 * @param {{
 *   orderId: string|undefined,
 *   searchParams: URLSearchParams,
 *   loginReturnPath: string,
 *   documentTitleSuffix?: string,
 *   className?: string,
 *   containerClassName?: string,
 * }} props
 */
const OrderTicketsView = ({
  orderId,
  searchParams,
  loginReturnPath,
  documentTitleSuffix,
  className = "",
  containerClassName = "",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /** Prefer ?token=; also read hash / alternate param names (SMS links). */
  const guestToken = useMemo(
    () =>
      guestTokenFromSearchParams(searchParams) ||
      guestTokenFromWindowLocation(),
    /* Re-run when URL changes (guest links / hash tokens). */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.* needed for hash/query edge cases
    [searchParams, location.pathname, location.search, location.hash]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState(() => ({ eventTitle: "", tickets: [] }));

  const venue = useMemo(
    () => pickEventVenue(view.tickets),
    [view.tickets]
  );
  const eventImage = useMemo(
    () => pickEventImageSrc(view.tickets),
    [view.tickets]
  );
  const dateLine = useMemo(() => {
    const raw = pickEventStartRaw(view.tickets);
    if (!raw) return "";
    const m = moment(raw);
    return m.isValid()
      ? `${m.format("ddd, D MMM")} | ${m.format("h:mm A")} ${t("MY_TICKETS_ONWARDS")}`
      : "";
  }, [view.tickets, t]);
  const bookingRefSummary = useMemo(
    () => pickBookingRefSummary(view.tickets),
    [view.tickets]
  );
  const qtyTypeLine = useMemo(
    () => pickQuantityTypeLine(view.tickets, t),
    [view.tickets, t]
  );

  const loadTickets = useCallback(async () => {
    if (!orderId || String(orderId).trim() === "") {
      setError(t("MY_TICKETS_INVALID_LINK"));
      setLoading(false);
      return;
    }
    const id = String(orderId).trim();
    setLoading(true);
    setError("");
    try {
      let raw;
      if (guestToken) {
        raw = await fetchOrderTicketsGuest(id, guestToken);
      } else {
        const accessToken = window.localStorage.getItem(
          Constant.LOCALSTORAGEKEYS.ACCESSTOKEN
        );
        if (!accessToken) {
          window.sessionStorage.setItem(
            Constant.SESSIONSTORAGEKEYS.MY_TICKETS_REDIRECT,
            loginReturnPath
          );
          navigate("/", { replace: true });
          setLoading(false);
          return;
        }
        raw = await fetchOrderTicketsWithTicketId(id);
      }
      const normalized = normalizeTicketsView(raw);
      setView(normalized);
    } catch (err) {
      const apiMsg =
        typeof err === "string"
          ? err.trim()
          : typeof err?.message === "string"
            ? err.message.trim()
            : "";
      const status =
        err != null && typeof err === "object" && "status" in err
          ? err.status
          : undefined;
      const code =
        err != null && typeof err === "object" && "code" in err
          ? err.code
          : undefined;
      let msg = apiMsg;

      if (code === "INVALID_ORDER_ID") {
        msg = t("MY_TICKETS_INVALID_LINK");
      } else if (code === "INVALID_TOKEN") {
        msg = t("MY_TICKETS_INVALID_TOKEN");
      } else if (status === 404) {
        const generic =
          !apiMsg ||
          /^request failed \(404\)$/i.test(apiMsg) ||
          /^not found$/i.test(apiMsg);
        msg = generic ? t("MY_TICKETS_LINK_EXPIRED_OR_INVALID") : apiMsg;
      } else if (status === 422) {
        msg = apiMsg || t("MY_TICKETS_VALIDATION_FAILED");
      } else {
        msg = apiMsg || t("MY_TICKETS_NOT_FOUND");
      }

      setError(msg);
      setView({ eventTitle: "", tickets: [] });
    } finally {
      setLoading(false);
    }
  }, [orderId, guestToken, loginReturnPath, navigate, t]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const title = [t("MY_TICKETS_PAGE_TITLE"), documentTitleSuffix]
    .filter(Boolean)
    .join(" · ");

  const hasTickets = !loading && !error && view.tickets.length > 0;

  return (
    <div className={`my-tickets-view ${className}`.trim()}>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container className={`my-tickets-inner ${containerClassName}`.trim()}>
        <main aria-label={t("MY_TICKETS_PAGE_TITLE")}>
        {loading ? (
          <div className="text-center py-5 my-tickets-loading">
            <Spinner animation="border" role="status" className="text-primary" />
            <p className="small text-muted mt-2 mb-0">{t("LOADING")}</p>
          </div>
        ) : error ? (
          <Card className="border-0 shadow-none my-tickets-state-card">
            <Card.Body className="text-center py-5">
              <p className="mb-0 text-muted">{error}</p>
            </Card.Body>
          </Card>
        ) : (
          <>
            {hasTickets ? (
              <>
                <header className="my-tickets-hero">
                  {eventImage ? (
                    <img
                      src={eventImage}
                      alt=""
                      className="my-tickets-hero-thumb"
                    />
                  ) : (
                    <div
                      className="my-tickets-hero-thumb my-tickets-hero-thumb--placeholder"
                      aria-hidden
                    >
                      <i className="bx bxs-music" />
                    </div>
                  )}
                  <div className="my-tickets-hero-text">
                    <h1 className="my-tickets-hero-title">
                      {view.eventTitle || t("MY_TICKETS_PAGE_TITLE")}
                    </h1>
                    {venue ? (
                      <p className="my-tickets-hero-venue">{venue}</p>
                    ) : null}
                  </div>
                </header>

                <section className="my-tickets-summary-card">
                  {dateLine ? (
                    <p className="my-tickets-summary-when">{dateLine}</p>
                  ) : null}
                  {bookingRefSummary ? (
                    <p className="my-tickets-summary-ids">
                      {t("MY_TICKETS_BOOKING_REF")}: {bookingRefSummary}
                    </p>
                  ) : null}
                  {qtyTypeLine ? (
                    <p className="my-tickets-summary-qty">{qtyTypeLine}</p>
                  ) : null}
                  <hr className="my-tickets-summary-hr" />
                  <p className="my-tickets-summary-hint">
                    <i className="bx bx-qr-scan" aria-hidden />
                    <span>{t("MY_TICKETS_SHOW_QR_AT_GATE")}</span>
                  </p>
                </section>
              </>
            ) : (
              <h1 className="h4 mb-3 text-center text-white">
                {t("MY_TICKETS_PAGE_TITLE")}
              </h1>
            )}

            {view.tickets.length === 0 ? (
              <Card className="border-0 shadow-none my-tickets-state-card">
                <Card.Body className="text-center py-4 text-muted small">
                  {t("MY_TICKETS_EMPTY")}
                </Card.Body>
              </Card>
            ) : (
              <EntryQrAccordion tickets={view.tickets} />
            )}
          </>
        )}
        </main>
      </Container>
    </div>
  );
};

export default OrderTicketsView;
